import type { StorefrontCategory } from '@/api/menu-list/storefront-menu-snapshot.types';
import type { MenuItemData } from '@/shared/ui/providers/menu-item-provider';

export type FeedRow =
    | {
          kind: 'header';
          key: string;
          category: StorefrontCategory;
          categoryIndex: number;
      }
    | {
          kind: 'items';
          key: string;
          category: StorefrontCategory;
          categoryIndex: number;
          items: MenuItemData[];
          /** Index of the first item in the full category item list. */
          startIndex: number;
          isLastInCategory: boolean;
      };

export type CategoryFeedInput = {
    category: StorefrontCategory;
    items: MenuItemData[];
};

/** Flatten categories into virtual rows: one header + chunked item rows. */
export const buildMenuFeedRows = (categories: CategoryFeedInput[], columnCount: number): FeedRow[] => {
    const cols = Math.max(1, columnCount);
    const rows: FeedRow[] = [];

    categories.forEach(({ category, items }, categoryIndex) => {
        rows.push({
            kind: 'header',
            key: `header:${category.id}`,
            category,
            categoryIndex,
        });

        for (let startIndex = 0; startIndex < items.length; startIndex += cols) {
            const chunk = items.slice(startIndex, startIndex + cols);
            const isLastInCategory = startIndex + cols >= items.length;
            rows.push({
                kind: 'items',
                key: `items:${category.id}:cols${cols}:${startIndex}`,
                category,
                categoryIndex,
                items: chunk,
                startIndex,
                isLastInCategory,
            });
        }
    });

    return rows;
};

export type FeedScrollAnchor =
    | { kind: 'item'; id: string; offsetWithinRow: number }
    | { kind: 'header'; id: string; offsetWithinRow: number };

/** Find row index for a scroll anchor after re-chunk (item may move rows). */
export const findFeedRowIndexForAnchor = (
    rows: FeedRow[],
    anchor: FeedScrollAnchor | null | undefined,
): number | null => {
    if (!anchor) return null;

    if (anchor.kind === 'header') {
        const index = rows.findIndex((row) => row.kind === 'header' && row.category.id === anchor.id);
        return index >= 0 ? index : null;
    }

    const index = rows.findIndex(
        (row) => row.kind === 'items' && row.items.some((item) => item.hash === anchor.id),
    );
    return index >= 0 ? index : null;
};
